import { useQuery } from '@apollo/client';
import type { FindTagLayoutsQuery, FindTagLayoutsQueryVariables } from 'types/graphql';

import PalletTagTemplateApi, { loadModalPageInput } from '../api/PalletTagTemplate.api';

export type TagLayout = NonNullable<NonNullable<FindTagLayoutsQuery['tagLayoutPage']['edges']>[number]>['node'];

/** Appends a "Show more" page to the pages already shown. */
const appendPage = (previous: FindTagLayoutsQuery, next: FindTagLayoutsQuery): FindTagLayoutsQuery => ({
  ...next,
  tagLayoutPage: {
    ...next.tagLayoutPage,
    edges: [...(previous.tagLayoutPage.edges ?? []), ...(next.tagLayoutPage.edges ?? [])],
  },
});

/** The pallet-tag load modal's read: one `tagLayoutPage` per search term; `fetchMore` appends the next page. */
export function useTagLayoutLoadPage(term: string) {
  const {
    data,
    fetchMore: fetchMorePages,
    loading,
    error,
  } = useQuery<FindTagLayoutsQuery, FindTagLayoutsQueryVariables>(PalletTagTemplateApi.FindLayouts, {
    variables: { input: loadModalPageInput(term) },
  });
  const layouts = (data?.tagLayoutPage.edges ?? []).flatMap((edge) => (edge ? [edge.node] : []));
  const pageInfo = data?.tagLayoutPage.pageInfo;
  const endCursor = pageInfo?.hasNextPage ? pageInfo.endCursor : null;

  const fetchMore = async () => {
    if (!endCursor) return;
    await fetchMorePages({
      variables: { input: loadModalPageInput(term, endCursor) },
      updateQuery: (previous, { fetchMoreResult }) => appendPage(previous, fetchMoreResult),
    });
  };

  return { layouts, hasNextPage: Boolean(endCursor), fetchMore, loading, error };
}
